/**
 * Languages shipped to users. French only for now; it is also the fallback. `pseudo` is a development language
 * that reveals hard-coded texts and overflows (accented, about 30% longer): never offered in production.
 */
export const supportedLanguages = ['fr'] as const;
export type Language = (typeof supportedLanguages)[number];
export type AnyLanguage = Language | 'pseudo';
export const defaultLanguage: Language = 'fr';
export const pseudoLanguage = 'pseudo' as const;

/** Intl locale used for formats (dates, numbers, plural rules). The pseudo language formats like French. */
export const formatLocale: Record<AnyLanguage, string> = { fr: 'fr-FR', pseudo: 'fr-FR' };

export function isSupportedLanguage(value: string): value is Language {
    return (supportedLanguages as readonly string[]).includes(value);
}

/** Reads `fr`, `fr-FR` or `fr_FR` as `fr`. */
export function toLanguage(value: string | null | undefined): Language | null {
    const language = value?.trim().toLowerCase().split(/[-_]/)[0];
    return language && isSupportedLanguage(language) ? language : null;
}

/**
 * Resolution order: the user's preference, the organization's language, then the browser (Accept-Language or
 * navigator.languages). Recipients without an account use their list or organization as `organization`.
 */
export function resolveLanguage(sources: {
    user?: string | null;
    organization?: string | null;
    browser?: readonly string[] | string | null;
}): Language {
    const browser =
        typeof sources.browser === 'string'
            ? sources.browser.split(',').map((part) => part.split(';')[0] ?? '')
            : (sources.browser ?? []);
    for (const candidate of [sources.user, sources.organization, ...browser]) {
        const language = toLanguage(candidate);
        if (language) {
            return language;
        }
    }
    return defaultLanguage;
}
